const { MinimogThemeStyles, MinimogThemeScripts } = window;

class QuickView {
  constructor() {
    this.modal = new MinimogTheme.Modal();
    this.isOpen = false;
    this.isLoading = false;
    const closeModal = this.modal.close;
    this.modal.close = (event) => {
      if (this.isClosing) return;
      if (!this.modal.modal.isConnected) return;
      this.isClosing = true;
      this.releaseAccessibility();
      closeModal(event);
    };
    const handleModalKeyDown = this.modal.handleKeyDown;
    this.modal.handleKeyDown = (event) => {
      if (!this.hasOtherDialog()) handleModalKeyDown(event);
    };

    // Event delegation for quick view button
    addEventDelegate({
      selector: ".m-product-quickview-button",
      handler: (e, target) => {
        e.preventDefault();
        if (this.isClosing) {
          this.pendingTarget = target;
          return;
        }
        this.openFromTrigger(target);
      },
    });

    // Subscribe to cart update events
    window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
      if (this.modal) this.modal.close();
    });
    window.MinimogEvents.subscribe('ON_MODAL_CLOSED', (modal) => {
      if (modal === this.modal) {
        this.isClosing = false;
        this.close();
        const pendingTarget = this.pendingTarget;
        this.pendingTarget = null;
        if (pendingTarget?.isConnected) this.openFromTrigger(pendingTarget);
      }
    });
  }

  openFromTrigger(target) {
    if (this.isLoading || this.isOpen || this.isClosing) return;
    const productHandle = target.dataset.productHandle;
    if (!productHandle) return;
    this.target = target;
    this.toggleLoading(true);
    this.loadProductQuickView(productHandle);
  }

  // Load product quick view HTML
  loadProductQuickView(productHandle) {
    if (this.isOpen) {
      this.modal && this.modal.close(); // Close any existing modal before loading a new one
    }
    this.isLoading = true;
    fetchSection("product-quickview", { url: `${window.MinimogSettings.base_url}products/${productHandle}` })
      .then((html) => {
        this.modalContent = html.querySelector("#MainProduct-quick-view__content");
        const productInfo = html.querySelector("product-info");
        const colorScheme = productInfo.dataset.colorScheme;
        this.modal.appendChild(this.modalContent);
        this.modal.setWidth("960px");

        const sectionWrapper = this.modal.modal.querySelector('#MainProduct-quick-view__content');

        sectionWrapper.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        this.modal.open();
        this.modal.setSizes(`m-gradient ${colorScheme}`);

        this.toggleLoading(false);
        this.isOpen = true;
        this.prepareAccessibility();
        this.isLoading = false; // Reset loading state
      })
      .then(() => {
        document.dispatchEvent(
          new CustomEvent('tcustomizer-custom-show-quick-view', {
            detail: { productHandle: this.target.dataset.productHandle }
          })
        );

        document.dispatchEvent(
          new CustomEvent("quick-view:loaded", {
            detail: { productUrl: this.target.dataset.productHandle }
          })
        );
      })
      .catch((error) => {
        this.toggleLoading(false);
        this.isLoading = false;
        console.error("Error loading product quick view:", error);
      });
  }


  prepareAccessibility() {
    const dialog = this.modal.modal;
    const title = this.modalContent.querySelector(".m-product-title");
    const closeButton = dialog.querySelector(".m-modal--close");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("tabindex", "-1");
    dialog.removeAttribute("aria-label");
    dialog.removeAttribute("aria-labelledby");
    if (title) {
      title.id = "QuickViewDialogTitle";
      dialog.setAttribute("aria-labelledby", title.id);
    } else {
      dialog.setAttribute("aria-label", this.modalContent.dataset.quickViewLabel);
    }
    closeButton.setAttribute("aria-label", this.modalContent.dataset.closeLabel);
    this.returnFocusTarget = this.target;
    this.focusGuard = (event) => {
      if (!this.isOpen || this.hasOtherDialog()) return;
      if (!dialog.contains(event.target)) closeButton.focus({ preventScroll: true });
    };
    this.tabGuard = (event) => {
      if (!this.isOpen || this.hasOtherDialog() || event.key !== "Tab") return;
      const items = Array.from(dialog.querySelectorAll(
        'a[href], button, input, select, textarea, [tabindex]'
      )).filter((element) =>
        element.tabIndex >= 0 && !element.matches(":disabled") &&
        !element.closest('[inert], [hidden], [aria-hidden="true"]') &&
        element.getClientRects().length && getComputedStyle(element).visibility !== "hidden"
      );
      const first = items[0] || dialog;
      const last = items[items.length - 1] || dialog;
      const active = document.activeElement;
      if (!dialog.contains(active) || active === dialog ||
          (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener("focusin", this.focusGuard);
    document.addEventListener("keydown", this.tabGuard);
    this.focusTimer = setTimeout(() => {
      if (this.isOpen && dialog.isConnected && !this.hasOtherDialog()) {
        closeButton.focus({ preventScroll: true });
      }
    }, this.modal.transitionDuration + 20);
  }

  hasOtherDialog() {
    return Array.from(document.querySelectorAll(
      '.pswp--open, dialog[open], [role="dialog"][aria-modal="true"], .m-open-modal'
    )).some((element) =>
      element !== this.modal.modal &&
      (element.matches('.pswp--open') || !this.modal.modal.contains(element)) &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getClientRects().length &&
      getComputedStyle(element).visibility !== "hidden"
    );
  }

  releaseAccessibility() {
    clearTimeout(this.focusTimer);
    document.removeEventListener("focusin", this.focusGuard);
    document.removeEventListener("keydown", this.tabGuard);
    const dialog = this.modal.modal;
    const active = document.activeElement;
    const restore = this.isOpen && !this.hasOtherDialog() &&
      (dialog.contains(active) || active === document.body);
    this.isOpen = false;
    if (restore && this.returnFocusTarget?.isConnected &&
        this.returnFocusTarget.getClientRects().length) {
      this.returnFocusTarget.focus({ preventScroll: true });
    }
    this.returnFocusTarget = null;
  }

  // Close the quick view modal
  close() {
    this.isOpen = false;
  }

  // Toggle loading spinner on the target element
  toggleLoading(isLoading) {
    if (isLoading) {
      this.target.classList.add("m-spinner-loading");
    } else {
      this.target.classList.remove("m-spinner-loading");
    }
  }
}

// Initialize the QuickView instance
MinimogTheme.ProductQuickView = new QuickView();

